import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
    RebelCard,
    RebelButton,
    RebelDangerButton,
    RebelGrid,
    RebelIcon,
    RebelEmptyState,
    RebelLoadingSpinner,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter
} from '../../../styles/rebel-experiences';
import { GetDB, generateGUID } from '../../../utils/database/firebase-budget-tool';
import { listenToCollection, deleteDocument } from '../../../utils/firestore-functions';

const AgendaConfigList = () => {
    const [locations, setLocations] = useState([]);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteLocation, setDeleteLocation] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const budgetDB = GetDB();

    useEffect(() => {
        const unsubscribeLocations = listenToCollection(budgetDB, "agenda-config-locations", (data) => {
            setLocations(data || []);
            setLoading(false);
        });

        const unsubscribeActivities = listenToCollection(budgetDB, "agenda-config-activities", (data) => {
            setActivities(data || []);
        });

        return () => {
            unsubscribeLocations();
            unsubscribeActivities();
        };
    }, [budgetDB]);

    const openLocation = (id) => {
        window.location = "/budget-app/agenda-config/edit/" + id;
    };

    const createNewLocation = () => {
        const newId = generateGUID();
        window.location = "/budget-app/agenda-config/edit/" + newId;
    };

    const confirmDelete = (location) => {
        setDeleteLocation(location);
        setDeleteModal(true);
    };

    const handleDelete = async () => {
        if (deleteLocation) {
            await deleteDocument(budgetDB, "agenda-config-locations", deleteLocation.id);

            // Also delete all activities for this location
            const locationActivities = activities.filter(act => act.location_id === deleteLocation.id);
            for (const activity of locationActivities) {
                await deleteDocument(budgetDB, "agenda-config-activities", activity.id);
            }

            setDeleteModal(false);
            setDeleteLocation(null);
        }
    };

    const getActivityCount = (locationId) => {
        return activities.filter(act => act.location_id === locationId).length;
    };

    const filteredLocations = locations.filter(loc =>
        loc.location_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.country?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <RebelCard>
                <div style={{ textAlign: 'center', padding: '48px' }}>
                    <RebelLoadingSpinner />
                    <p style={{ marginTop: '16px', color: '#b0b0b0' }}>Loading Locations...</p>
                </div>
            </RebelCard>
        );
    }

    return (
        <React.Fragment>
            <RebelCard>
                <HeaderContainer>
                    <SearchInput
                        type="text"
                        placeholder="Search locations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <RebelButton onClick={createNewLocation}>
                        <RebelIcon className="fas fa-plus" style={{ marginRight: '8px' }} />
                        Add Location
                    </RebelButton>
                </HeaderContainer>

                <RebelGrid>
                    {filteredLocations.length === 0 ? (
                        <RebelEmptyState>
                            <RebelIcon className="fas fa-map-marker-alt" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                            <p>No locations found. Click "Add Location" to create your first location.</p>
                        </RebelEmptyState>
                    ) : (
                        filteredLocations.map((location) => {
                            const activityCount = getActivityCount(location.id);
                            return (
                                <LocationCard key={location.id}>
                                    {location.location_image_url && (
                                        <CardImage src={location.location_image_url} alt={location.location_name} />
                                    )}
                                    <CardContent>
                                        <CardTitle>{location.location_name || 'Unnamed Location'}</CardTitle>
                                        <CardInfo>
                                            <InfoRow>
                                                <RebelIcon className="fas fa-globe" />
                                                <span>{location.country || 'N/A'}</span>
                                            </InfoRow>
                                            <InfoRow>
                                                <RebelIcon className="fas fa-dollar-sign" />
                                                <span>{location.currency || 'USD'}</span>
                                            </InfoRow>
                                            <InfoRow>
                                                <RebelIcon className="fas fa-clock" />
                                                <span>{location.timezone || 'N/A'}</span>
                                            </InfoRow>
                                            <InfoRow>
                                                <RebelIcon className="fas fa-calendar-day" />
                                                <span>{activityCount} {activityCount === 1 ? 'Activity' : 'Activities'}</span>
                                            </InfoRow>
                                        </CardInfo>
                                        {location.description && (
                                            <CardDescription>{location.description}</CardDescription>
                                        )}
                                        <CardActions>
                                            <RebelButton onClick={() => openLocation(location.id)} size="sm">
                                                <RebelIcon className="fas fa-edit" style={{ marginRight: '6px' }} />
                                                Edit
                                            </RebelButton>
                                            <RebelDangerButton onClick={() => confirmDelete(location)} size="sm">
                                                <RebelIcon className="fas fa-trash" style={{ marginRight: '6px' }} />
                                                Delete
                                            </RebelDangerButton>
                                        </CardActions>
                                    </CardContent>
                                </LocationCard>
                            );
                        })
                    )}
                </RebelGrid>
            </RebelCard>

            <RebelModal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <RebelModalHeader toggle={() => setDeleteModal(false)}>
                    Confirm Delete
                </RebelModalHeader>
                <RebelModalBody>
                    Are you sure you want to delete this location?
                    <br /><br />
                    <strong>{deleteLocation?.location_name || 'This location'}</strong>
                    <br /><br />
                    {getActivityCount(deleteLocation?.id) > 0 && (
                        <>
                            <WarningText>
                                ⚠️ This will also delete {getActivityCount(deleteLocation?.id)} {getActivityCount(deleteLocation?.id) === 1 ? 'activity' : 'activities'} associated with this location.
                            </WarningText>
                            <br />
                        </>
                    )}
                    This action cannot be undone.
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelButton onClick={() => setDeleteModal(false)}>
                        Cancel
                    </RebelButton>
                    <RebelDangerButton onClick={handleDelete}>
                        Delete
                    </RebelDangerButton>
                </RebelModalFooter>
            </RebelModal>
        </React.Fragment>
    );
};

export default AgendaConfigList;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    gap: 16px;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const SearchInput = styled.input`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #ffffff;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 14px;
    flex: 1;
    max-width: 400px;

    &:focus {
        outline: none;
        border-color: #667eea;
        background: rgba(255, 255, 255, 0.08);
    }

    &::placeholder {
        color: #666;
    }

    @media (max-width: 768px) {
        max-width: 100%;
    }
`;

const LocationCard = styled.div`
    background: linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(40, 40, 40, 0.95) 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(102, 126, 234, 0.2);
        border-color: rgba(102, 126, 234, 0.5);
    }
`;

const CardImage = styled.img`
    width: 100%;
    height: 180px;
    object-fit: cover;
    background: rgba(255, 255, 255, 0.05);
`;

const CardContent = styled.div`
    padding: 20px;
`;

const CardTitle = styled.h6`
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 12px 0;
`;

const CardInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
`;

const InfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: #b0b0b0;
    font-size: 13px;

    i {
        width: 16px;
        color: #667eea;
        font-size: 12px;
    }
`;

const CardDescription = styled.p`
    color: #888;
    font-size: 13px;
    margin: 12px 0;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const CardActions = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 16px;
`;

const WarningText = styled.div`
    color: #fdb506;
    padding: 10px;
    background: rgba(253, 181, 6, 0.1);
    border-radius: 4px;
    border-left: 3px solid #fdb506;
`;
